import Phaser from '../lib/phaser.js';
import { BattleMenu } from '../battle/ui/menu/battle-menu.js';
import { typeMultiplier, effectivenessLabel, attackAtLevel } from '../battle/type-chart.js';
import { SCENE_KEYS } from './scene-keys.js';
import { DIRECTION } from '../common/direction.js';
import { EnemyBattleMonster } from '../battle/monsters/enemy-battle-monster.js';
import { PlayerBattleMonster } from '../battle/monsters/player-battle-monster.js';
import { StateMachine } from '../utils/state-machine.js';
import { Background } from '../battle/background.js';
import { ATTACK_TARGET, AttackManager } from '../battle/attacks/attack-manager.js';
import { createSceneTransition } from '../utils/scene-transition.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { XP_PER_ENEMY_LEVEL } from '@mn/shared';
import { BATTLE_SCENE_OPTIONS } from '../common/options.js';
import { BaseScene } from './base-scene.js';
import { DataUtils } from '../utils/data-utils.js';
import { AUDIO_ASSET_KEYS, BATTLE_ASSET_KEYS } from '../assets/asset-keys.js';
import { playBackgroundMusic, playSoundFx } from '../utils/audio-utils.js';
import { calculateExpGainedFromMonster, handleMonsterGainingExperience } from '../utils/leveling-utils.js';
import { ITEM_CATEGORY } from '../types/typedef.js';
import { exhaustiveGuard } from '../utils/guard.js';
import { Ball } from '../battle/ball.js';
import { sleep } from '../utils/time-utils.js';
import { generateUuid } from '../utils/random.js';
import { calculateMonsterCaptureResults } from '../utils/catch-utils.js';
import { EnemyBattleNpc } from '../battle/enemy-battle-npc.js';
import { netManager } from '../net/index.js';
import { postBattleWin, postQuestProgress, resolveBattleSession, saveParty } from '../net/api.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';

/**
 * The battle UI is authored against a fixed design resolution (the forest battle background is
 * exactly 1024x576, and every sprite/menu/health-bar coordinate is laid out in that space). The
 * game runs Phaser.Scale.RESIZE at the full window size, so without a transform the design-space
 * layout only fills a 1024x576 chunk of the (much larger) viewport — the "battle fills 1/4 of the
 * screen" bug. We keep the whole layout in design space and use the scene camera to scale + center
 * that space to COVER the viewport, re-fitting on every RESIZE. Mirrors how the overworld scales
 * its camera to the window.
 */
const BATTLE_DESIGN_WIDTH = 1024;
const BATTLE_DESIGN_HEIGHT = 576;

const BATTLE_STATES = Object.freeze({
  INTRO: 'INTRO',
  PRE_BATTLE_INFO: 'PRE_BATTLE_INFO',
  PRE_BATTLE_INFO_NPC: 'PRE_BATTLE_INFO_NPC',
  BRING_OUT_MONSTER: 'BRING_OUT_MONSTER',
  PLAYER_INPUT: 'PLAYER_INPUT',
  ENEMY_INPUT: 'ENEMY_INPUT',
  BATTLE: 'BATTLE',
  POST_ATTACK_CHECK: 'POST_ATTACK_CHECK',
  FINISHED: 'FINISHED',
  FLEE_ATTEMPT: 'FLEE_ATTEMPT',
  GAIN_EXPERIENCE: 'GAIN_EXPERIENCE',
  SWITCH_MONSTER: 'SWITCH_MONSTER',
  NPC_SWITCH_MONSTER: 'NPC_SWITCH_MONSTER',
  USED_ITEM: 'USED_ITEM',
  HEAL_ITEM_USED: 'HEAL_ITEM_USED',
  CAPTURE_ITEM_USED: 'CAPTURE_ITEM_USED',
  CAUGHT_MONSTER: 'CAUGHT_MONSTER',
});

/**
 * @typedef BattleSceneData
 * @type {object}
 * @property {import('../types/typedef.js').Monster[]} playerMonsters
 * @property {import('../types/typedef.js').Monster[]} enemyMonsters
 * @property {boolean} [isTrainerBattle=false]
 * @property {object} [npc]
 * @property {number} npc.id
 * @property {string} npc.assetKey
 * @property {string} npc.name
 * @property {string[]} npc.trainerLostMessages
 * @property {string} [battleSessionId] single-use server battle-session token (wild battles only). When
 *   present, the win/capture reward is granted server-authoritatively via /battle/resolve; absent =>
 *   the legacy local + /player/battle-win path is used (offline / trainer battles / server down).
 */

/**
 * @typedef BattleSceneWasResumedData
 * @type {object}
 * @property {boolean} wasMonsterSelected
 * @property {number} [selectedMonsterIndex]
 * @property {boolean} wasItemUsed
 * @property {import('../types/typedef.js').Item} [item]
 */

export class BattleScene extends BaseScene {
  /** @type {BattleMenu} */
  #battleMenu;
  /** @type {boolean} true when this battle is underground — enables the cave-ceiling crush on a KO */
  #inCave = false;
  /** @type {EnemyBattleMonster} */
  #activeEnemyMonster;
  /** @type {PlayerBattleMonster} */
  #activePlayerMonster;
  /** @type {number} */
  #activePlayerAttackIndex;
  /** @type {StateMachine} */
  #battleStateMachine;
  /** @type {AttackManager} */
  #attackManager;
  /** @type {boolean} */
  #skipAnimations;
  /** @type {number} */
  #activeEnemyAttackIndex;
  /** @type {BattleSceneData} */
  #sceneData;
  /** @type {number} */
  #activePlayerMonsterPartyIndex;
  /** @type {boolean} */
  #playerKnockedOut;
  /** @type {boolean} */
  #switchingActiveMonster;
  /** @type {boolean} */
  #activeMonsterKnockedOut;
  /** @type {Phaser.GameObjects.Container} */
  #availableMonstersUiContainerForPlayer;
  /** @type {boolean} */
  #monsterCaptured;
  /** @type {Ball} */
  #ball;
  /** @type {boolean} */
  #isTrainerBattle;
  /** @type {number} */
  #activeEnemyMonsterPartyIndex;
  /** @type {Phaser.GameObjects.Container | undefined } */
  #availableMonstersUiContainerForNpc;
  /** @type {EnemyBattleNpc | undefined} */
  #enemyBattleNpc;
  /** @type {boolean} guards against awarding battle-win Gold more than once per battle */
  #goldAwarded;
  /** @type {string | undefined} last state-machine state the watchdog observed */
  #watchdogLastStateName;
  /** @type {boolean} last input-locked value the watchdog observed */
  #watchdogLastInputLocked;
  /** @type {number} timestamp (scene time) of the last observed progress (state change / unlock) */
  #watchdogLastProgressAt;
  /** @type {number} number of times the watchdog has force-recovered this battle (escalation guard) */
  #watchdogRecoveries;
  /** @type {number} consecutive no-op confirms at the move/main menu (wedge breaker) */
  #wedgeConfirms = 0;

  constructor() {
    super({
      key: SCENE_KEYS.BATTLE_SCENE,
    });
  }

  /**
   * @param {BattleSceneData} data
   * @returns {void}
   */
  init(data) {
    super.init(data);
    this.#sceneData = data;

    // HARDEN: strip any null/invalid party slots before the battle runs. The faint/switch/wipe logic
    // reads `monster.id` / `monster.currentHp` across the party (e.g. the "has other active monsters?"
    // check), so a single null slot threw mid-KO and FROZE the battle — "can't switch pets, no response
    // to buttons after being beaten." A corrupted party can reach here from a bad save.
    if (this.#sceneData && Array.isArray(this.#sceneData.playerMonsters)) {
      this.#sceneData.playerMonsters = this.#sceneData.playerMonsters.filter(
        (m) => m && typeof m.monsterId === 'number',
      );
    }

    // added for testing from preload scene
    if (Object.keys(data).length === 0) {
      this.#sceneData = {
        enemyMonsters: [DataUtils.getMonsterById(this, 2)],
        playerMonsters: [...dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY)],
        isTrainerBattle: false,
        npc: undefined,
      };
    }

    this.#isTrainerBattle = this.#sceneData.isTrainerBattle || false;
    this.#activePlayerAttackIndex = -1;
    this.#activeEnemyAttackIndex = -1;
    this.#activePlayerMonsterPartyIndex = 0;
    this.#activeEnemyMonsterPartyIndex = 0;
    this.#skipAnimations = true;
    this.#playerKnockedOut = false;
    this.#switchingActiveMonster = false;
    this.#activeMonsterKnockedOut = false;
    this.#monsterCaptured = false;
    this.#availableMonstersUiContainerForNpc = undefined;
    this.#enemyBattleNpc = undefined;
    this.#goldAwarded = false;
    this.#watchdogLastStateName = undefined;
    this.#watchdogLastInputLocked = true;
    this.#watchdogLastProgressAt = 0;
    this.#watchdogRecoveries = 0;

    /** @type {import('../common/options.js').BattleSceneMenuOptions | undefined} */
    const chosenBattleSceneOption = dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_BATTLE_SCENE_ANIMATIONS);
    if (chosenBattleSceneOption === undefined || chosenBattleSceneOption === BATTLE_SCENE_OPTIONS.ON) {
      this.#skipAnimations = false;
    }
  }

  /**
   * @returns {void}
   */
  create() {
    super.create();

    // Make the fixed 1024x576 design-space battle layout fill the actual viewport, and keep it
    // filling on window resize. Without this the battle only occupies a 1024x576 corner of the
    // RESIZE-mode canvas.
    this.#fitCameraToViewport();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#fitCameraToViewport, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.#fitCameraToViewport, this);
    });

    // create main background — the Cave (badlands) uses its own cavern backdrop; everywhere else
    // gets the grassland arena. Detect from the realm/area passed by the world scene.
    const background = new Background(this);
    const where = `${this.#sceneData.realm ?? ''} ${this.#sceneData.area ?? ''}`;
    const inCave = /cave|badlands/i.test(where);
    const inTown = !inCave && /town|main_1/i.test(where);
    this.#inCave = inCave;
    if (inCave) background.showCave();
    else if (inTown) background.showTown();
    else background.showForest();
    this.#createBattleAmbience(inCave);

    // create the player and enemy monsters
    this.#activeEnemyMonster = new EnemyBattleMonster({
      scene: this,
      monsterDetails: this.#sceneData.enemyMonsters[this.#activeEnemyMonsterPartyIndex],
      skipBattleAnimations: this.#skipAnimations,
    });
    // find first monster in party that is able to battle. CRITICAL: findIndex returns -1 when NO
    // monster has HP (all fainted / a desync), and playerMonsters[-1] is undefined → the
    // PlayerBattleMonster constructor then crashed the whole battle create, freezing the player on
    // the map with no battle. Fall back to the first party monster so the index is never -1/undefined.
    const eligibleMonsterIndex = this.#sceneData.playerMonsters.findIndex((monster) => monster && monster.currentHp > 0);
    this.#activePlayerMonsterPartyIndex = eligibleMonsterIndex >= 0 ? eligibleMonsterIndex : 0;
    const activePlayerMonsterDetails = this.#sceneData.playerMonsters[this.#activePlayerMonsterPartyIndex];
    if (!activePlayerMonsterDetails) {
      // Empty/invalid party — can't battle. Bail back to the world instead of crashing (frozen map).
      console.error('[battle] no valid player monster — returning to world');
      this.scene.start(SCENE_KEYS.WORLD_SCENE, {});
      return;
    }
    this.#activePlayerMonster = new PlayerBattleMonster({
      scene: this,
      monsterDetails: activePlayerMonsterDetails,
      skipBattleAnimations: this.#skipAnimations,
    });

    // create the enemy npc if trainer battle
    if (this.#isTrainerBattle) {
      this.#enemyBattleNpc = new EnemyBattleNpc({
        scene: this,
        assetKey: this.#sceneData.npc.assetKey,
        skipBattleAnimations: this.#skipAnimations,
      });
    }

    // render out the main info and sub info panes
    this.#battleMenu = new BattleMenu(this, this.#activePlayerMonster, this.#skipAnimations, this.#isTrainerBattle);
    // make the action menu (Fight / Switch / Item / Flee) and move selection mouse/touch clickable
    this.#battleMenu.enablePointerInput(() => this.#processPlayerSelectionAfterConfirm());
    // a click anywhere advances message gates (intro / post-attack / etc.) exactly like SPACE
    this.input.on(Phaser.Input.Events.POINTER_DOWN, () => this.#handlePointerAdvance());
    this.#createBattleStateMachine();
    this.#attackManager = new AttackManager(this, this.#skipAnimations);
    this.#createAvailableMonstersUi();
    this.#ball = new Ball({
      scene: this,
      assetKey: BATTLE_ASSET_KEYS.DAMAGED_BALL,
      assetFrame: 0,
      // damagedBall.png is now a crisp 64px sprite (was a 512px render at 0.1). 64×0.8 ≈ 512×0.1, so
      // the on-screen capture-ball size is unchanged while staying sharp.
      scale: 0.8,
      skipBattleAnimations: this.#skipAnimations,
    });

    this._controls.lockInput = true;

    // add audio
    playBackgroundMusic(this, AUDIO_ASSET_KEYS.BATTLE);
  }

  /**
   * @returns {void}
   */
  update() {
    super.update();

    this.#battleStateMachine.update();

    // Watchdog — a hard safety net against the "permanently stuck in battle" bug. The battle is a
    // chain of animation/damage callbacks; if any link's completion callback fails to fire the
    // scene stays input-locked (or parked out of PLAYER_INPUT) forever. We track the last moment we
    // saw *progress* (a state-machine state change or input being unlocked) and, if nothing has
    // progressed for too long, force-recover so the player is never trapped.
    this.#runWatchdog();

    if (this._controls.isInputLocked) {
      return;
    }

    const wasSpaceKeyPressed = this._controls.wasSpaceKeyPressed();
    // limit input based on the current battle state we are in
    // if we are not in the right battle state, return early and do not process input
    if (
      wasSpaceKeyPressed &&
      (this.#battleStateMachine.currentStateName === BATTLE_STATES.PRE_BATTLE_INFO ||
        this.#battleStateMachine.currentStateName === BATTLE_STATES.PRE_BATTLE_INFO_NPC ||
        this.#battleStateMachine.currentStateName === BATTLE_STATES.POST_ATTACK_CHECK ||
        this.#battleStateMachine.currentStateName === BATTLE_STATES.GAIN_EXPERIENCE ||
        this.#battleStateMachine.currentStateName === BATTLE_STATES.SWITCH_MONSTER ||
        this.#battleStateMachine.currentStateName === BATTLE_STATES.NPC_SWITCH_MONSTER ||
        this.#battleStateMachine.currentStateName === BATTLE_STATES.CAPTURE_ITEM_USED ||
        this.#battleStateMachine.currentStateName === BATTLE_STATES.FLEE_ATTEMPT)
    ) {
      this.#battleMenu.handlePlayerInput('OK');
      return;
    }
    if (this.#battleStateMachine.currentStateName !== BATTLE_STATES.PLAYER_INPUT) {
      return;
    }

    if (wasSpaceKeyPressed) {
      const sigBefore = this.#battleMenu.menuStateSignature;
      this.#battleMenu.handlePlayerInput('OK');
      this.#processPlayerSelectionAfterConfirm();
      this.#breakMenuWedgeIfStuck(sigBefore);
      return;
    }

    if (this._controls.wasBackKeyPressed()) {
      this.#battleMenu.handlePlayerInput('CANCEL');
      return;
    }

    const selectedDirection = this._controls.getDirectionKeyJustPressed();
    if (selectedDirection !== DIRECTION.NONE) {
      this.#battleMenu.handlePlayerInput(selectedDirection);
    }
  }

  /**
   * Hard safety net against the battle becoming permanently stuck. "Progress" = the state machine
   * changed state OR input transitioned from locked → unlocked. While we keep seeing progress the
   * watchdog stays quiet. If we go > 9 seconds with NO progress AND the player can't act (input is
   * locked, or we're parked in a non-PLAYER_INPUT state), we force-recover:
   *   1st recovery → nudge the current message gate / menu forward with an 'OK' and unlock input.
   *   2nd recovery (still stuck) → push the state machine toward FINISHED so the battle ALWAYS ends
   *   and returns control to the overworld. The player must NEVER be permanently trapped.
   * Once we're sitting in PLAYER_INPUT with input unlocked, there's nothing to recover from.
   * @returns {void}
   */
  #runWatchdog() {
    const now = this.time.now;
    const stateName = this.#battleStateMachine.currentStateName;
    const inputLocked = this._controls.isInputLocked;

    // record progress whenever the state changed or input just became available
    const stateChanged = stateName !== this.#watchdogLastStateName;
    const becameUnlocked = this.#watchdogLastInputLocked && !inputLocked;
    if (this.#watchdogLastProgressAt === 0 || stateChanged || becameUnlocked) {
      this.#watchdogLastProgressAt = now;
      this.#watchdogLastStateName = stateName;
      this.#watchdogLastInputLocked = inputLocked;
      return;
    }
    this.#watchdogLastStateName = stateName;
    this.#watchdogLastInputLocked = inputLocked;

    // already finished / transitioning out — nothing to guard
    if (stateName === BATTLE_STATES.FINISHED) {
      return;
    }

    // The player is free to act and the menu is up — this is a healthy idle state, not a hang.
    if (stateName === BATTLE_STATES.PLAYER_INPUT && !inputLocked) {
      this.#watchdogLastProgressAt = now;
      return;
    }

    // SWITCH_MONSTER intentionally pauses this scene while the party overlay is open; don't trip.
    if (this.scene.isPaused(SCENE_KEYS.BATTLE_SCENE)) {
      this.#watchdogLastProgressAt = now;
      return;
    }

    // Stuck detection covers BOTH failure modes:
    //  • input LOCKED (an animation/damage callback that never fired) → recover fast (8s).
    //  • input UNLOCKED but parked in a non-player-input MESSAGE GATE that won't advance — e.g. the
    //    capture "breaks free" / exp gate (the "stuck after throwing a ball" bug) → recover after a
    //    generous window (15s) so a slow reader who taps resets the clock first, but a genuinely dead
    //    gate is still rescued. (PLAYER_INPUT + paused are treated as healthy above, so the move menu
    //    is never auto-advanced.) Recovery NUDGES the gate (= a tap), it does not end the battle until
    //    several failed nudges, so a transient hiccup never yanks the player out mid-fight.
    const STUCK_THRESHOLD_MS = inputLocked ? 8000 : 15000;
    if (now - this.#watchdogLastProgressAt < STUCK_THRESHOLD_MS) {
      return;
    }

    this.#watchdogRecoveries += 1;
    console.warn(
      `[battle-watchdog] no progress for >${STUCK_THRESHOLD_MS}ms in state "${stateName}" (locked=${inputLocked}); ` +
        `force-recover #${this.#watchdogRecoveries}`
    );
    // reset the clock so we wait another full window before escalating again
    this.#watchdogLastProgressAt = now;

    // Prefer NUDGING over ending: free input + advance the gate exactly like SPACE, and retry this
    // several times (each waits a full window) before ever force-ending — so a transient hiccup
    // recovers in-battle instead of yanking the player out ("leaving battles mid-battle").
    if (this.#watchdogRecoveries < 4) {
      this._controls.lockInput = false;
      this.#battleMenu.handlePlayerInput('OK');
      return;
    }

    // Truly dead after ~48s of nudges — last resort: end the battle so the player isn't trapped.
    this._controls.lockInput = false;
    this.#battleStateMachine.setState(BATTLE_STATES.FINISHED);
  }

  /**
   * Wedge breaker for the move/main menu. The watchdog treats PLAYER_INPUT as a healthy idle state, so
   * a desynced menu that silently ignores confirms would trap the player forever (the reported "stuck
   * at the move menu, SPACE/TAP does nothing" bug). A NORMAL confirm always changes the menu signature
   * (open the sub-menu, pick a move, or advance a message) or the battle state; a wedged one changes
   * nothing. After 3 consecutive no-op confirms we force the menu back to a clean FIGHT state so the
   * player can always act. The counter resets the instant anything progresses.
   * @param {string} sigBefore the menu signature captured immediately before the confirm
   * @returns {void}
   */
  #breakMenuWedgeIfStuck(sigBefore) {
    const stillPlayerInput = this.#battleStateMachine.currentStateName === BATTLE_STATES.PLAYER_INPUT;
    const changed = this.#battleMenu.menuStateSignature !== sigBefore;
    if (stillPlayerInput && !changed) {
      this.#wedgeConfirms += 1;
      if (this.#wedgeConfirms >= 3) {
        this.#wedgeConfirms = 0;
        console.warn('[battle] move menu wedged (no-op confirms) — resetting to main menu');
        this.#battleMenu.forceResetToMainMenu();
      }
      return;
    }
    this.#wedgeConfirms = 0;
  }

  /**
   * Click/tap handler for the message-gate battle states (intro, post-attack, exp, flee, capture,
   * switch prompts). Mirrors the SPACE-key handling in update() so the battle can be advanced
   * entirely by mouse/touch. The action menu buttons handle their own clicks (and stop propagation),
   * so this only advances "press to continue" message gates — never the PLAYER_INPUT menu itself.
   * @returns {void}
   */
  #handlePointerAdvance() {
    if (this._controls.isInputLocked) {
      return;
    }
    // While in PLAYER_INPUT the info pane can be showing a "press to continue" message gate (e.g.
    // "You used the following item: …" after a ball/potion/revive). SPACE advances it via the
    // PLAYER_INPUT branch in update(); mirror that for tap/click. handlePointerConfirmMessage only
    // fires while the menu is waiting for input (never on the FIGHT/SWITCH/ITEM/FLEE or move menu),
    // and when it consumes a message we run the same post-confirm checks as the SPACE path so the
    // turn advances to ENEMY_INPUT exactly like a keyboard confirm.
    if (this.#battleStateMachine.currentStateName === BATTLE_STATES.PLAYER_INPUT) {
      if (this.#battleMenu.handlePointerConfirmMessage()) {
        this.#processPlayerSelectionAfterConfirm();
      }
      return;
    }
    if (
      this.#battleStateMachine.currentStateName === BATTLE_STATES.PRE_BATTLE_INFO ||
      this.#battleStateMachine.currentStateName === BATTLE_STATES.PRE_BATTLE_INFO_NPC ||
      this.#battleStateMachine.currentStateName === BATTLE_STATES.POST_ATTACK_CHECK ||
      this.#battleStateMachine.currentStateName === BATTLE_STATES.GAIN_EXPERIENCE ||
      this.#battleStateMachine.currentStateName === BATTLE_STATES.SWITCH_MONSTER ||
      this.#battleStateMachine.currentStateName === BATTLE_STATES.NPC_SWITCH_MONSTER ||
      this.#battleStateMachine.currentStateName === BATTLE_STATES.CAPTURE_ITEM_USED ||
      this.#battleStateMachine.currentStateName === BATTLE_STATES.FLEE_ATTEMPT
    ) {
      this.#battleMenu.handlePlayerInput('OK');
    }
  }

  /**
   * Runs the post-confirm checks after a select/OK has been forwarded to the battle menu while in
   * PLAYER_INPUT (item used / flee / switch / attack chosen → drive the state machine). Shared by
   * the SPACE key and mouse/touch clicks so both inputs behave identically.
   * @returns {void}
   */
  #processPlayerSelectionAfterConfirm() {
    if (this.#battleStateMachine.currentStateName !== BATTLE_STATES.PLAYER_INPUT) {
      return;
    }

    // check if the player used an item
    if (this.#battleMenu.wasItemUsed) {
      this.#battleStateMachine.setState(BATTLE_STATES.ENEMY_INPUT);
      return;
    }

    // check if the player attempted to flee
    if (this.#battleMenu.isAttemptingToFlee) {
      this.#battleStateMachine.setState(BATTLE_STATES.FLEE_ATTEMPT);
      return;
    }

    // check if the player attempted to switch monsters
    if (this.#battleMenu.isAttemptingToSwitchMonsters) {
      this.#battleStateMachine.setState(BATTLE_STATES.SWITCH_MONSTER);
      return;
    }

    // check if the player selected an attack, and start battle sequence for the fight
    if (this.#battleMenu.selectedAttack === undefined) {
      return;
    }
    this.#activePlayerAttackIndex = this.#battleMenu.selectedAttack;

    // ALWAYS advance the turn here — even if attacks[index] is missing/out-of-range. The old code
    // returned silently when the selected move didn't resolve into attacks[] (e.g. a move the menu
    // shows by name whose attackId never built into the BattleMonster's attacks list, like a high-tier
    // move). That left the scene parked in PLAYER_INPUT, which the watchdog treats as a HEALTHY idle
    // state → it never recovers → the reported "permanently stuck at the move menu, SPACE/TAP does
    // nothing" soft-lock. #playerAttack() resolves a usable attack via #resolveAttack(), so advancing
    // is always safe; never bail without changing state.
    this.#battleMenu.hideMonsterAttackSubMenu();
    this.#battleStateMachine.setState(BATTLE_STATES.ENEMY_INPUT);
  }

  /**
   * Safely resolve an attack for a battle monster, guarding against an out-of-range / undefined
   * `attacks[index]` (the roster can have monsters with fewer moves than expected, or
   * `pickRandomMove()` returning an index for an empty move list). Always returns a usable attack so
   * the attack → damage → callback chain can never break on a missing move (a primary cause of the
   * permanent input-lock). Falls back to the monster's first attack, then to a basic SLASH attack.
   * @param {import('../battle/monsters/battle-monster.js').BattleMonster} monster
   * @param {number} index
   * @returns {{ name: string, animationName: string, audioKey: string }}
   */
  #resolveAttack(monster, index) {
    const attacks = monster?.attacks ?? [];
    const attack = attacks[index] ?? attacks[0];
    if (attack) {
      return attack;
    }
    // ultimate fallback: a basic attack so the chain still completes
    return { name: 'Tackle', animationName: 'SLASH', audioKey: 'CLAW' };
  }

  /**
   * @param {() => void} callback
   * @returns {void}
   */
  #playerAttack(callback) {
    const done = typeof callback === 'function' ? callback : () => {};
    if (this.#activePlayerMonster.isFainted) {
      done();
      return;
    }

    const attack = this.#resolveAttack(this.#activePlayerMonster, this.#activePlayerAttackIndex);
    this.#battleMenu.updateInfoPaneMessageNoInputRequired(
      `${this.#activePlayerMonster.name} used ${attack.name}`,
      () => {
        // play attack animation based on the selected attack
        // when attack is finished, play damage animation and then update health bar
        this.time.delayedCall(500, () => {
          this.time.delayedCall(100, () => {
            playSoundFx(this, attack.audioKey);
          });
          this.#attackManager.playAttackAnimation(
            attack.animationName,
            ATTACK_TARGET.ENEMY,
            () => {
              this.#activeEnemyMonster.playTakeDamageAnimation(() => {
                const mult = typeMultiplier(this.#activePlayerMonster.element, this.#activeEnemyMonster.element);
                const atk = attackAtLevel(this.#activePlayerMonster.baseAttack, this.#activePlayerMonster.level);
                const dmg = Math.max(1, Math.round(atk * (attack.power ?? 1) * mult));
                this.#activeEnemyMonster.takeDamage(dmg, () => this.#afterHit(mult, done));
              });
            },
            this.#activePlayerMonster.element
          );
        });
      }
    );
  }

  /**
   * After a hit lands, briefly caption "super effective / not very effective" (skipped for a neutral
   * hit), then continue the battle flow.
   * @param {number} mult
   * @param {() => void} done
   * @returns {void}
   */
  #afterHit(mult, done) {
    const label = effectivenessLabel(mult);
    if (label) {
      this.#battleMenu.updateInfoPaneMessageNoInputRequired(label, () => done());
    } else {
      done();
    }
  }

  /**
   * @param {() => void} callback
   * @returns {void}
   */
  #enemyAttack(callback) {
    const done = typeof callback === 'function' ? callback : () => {};
    if (this.#activeEnemyMonster.isFainted) {
      done();
      return;
    }

    const attack = this.#resolveAttack(this.#activeEnemyMonster, this.#activeEnemyAttackIndex);
    this.#battleMenu.updateInfoPaneMessageNoInputRequired(`foe ${this.#activeEnemyMonster.name} used ${attack.name}`, () => {
      // play attack animation based on the selected attack
      // when attack is finished, play damage animation and then update health bar
      this.time.delayedCall(500, () => {
        this.time.delayedCall(100, () => {
          playSoundFx(this, attack.audioKey);
        });
        this.#attackManager.playAttackAnimation(
          attack.animationName,
          ATTACK_TARGET.PLAYER,
          () => {
            this.#activePlayerMonster.playTakeDamageAnimation(() => {
              const mult = typeMultiplier(this.#activeEnemyMonster.element, this.#activePlayerMonster.element);
              const atk = attackAtLevel(this.#activeEnemyMonster.baseAttack, this.#activeEnemyMonster.level);
              const dmg = Math.max(1, Math.round(atk * (attack.power ?? 1) * mult));
              this.#activePlayerMonster.takeDamage(dmg, () => this.#afterHit(mult, done));
            });
          },
          this.#activeEnemyMonster.element
        );
      });
    });
  }

  /**
   * @returns {void}
   */
  #postBattleSequenceCheck() {
    this._controls.lockInput = true;

    // update monster details in scene data and data manager to align with changes from battle
    this.#sceneData.playerMonsters[this.#activePlayerMonsterPartyIndex].currentHp = this.#activePlayerMonster.currentHp;
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, this.#sceneData.playerMonsters);
    this.#sceneData.enemyMonsters[this.#activeEnemyMonsterPartyIndex].currentHp = this.#activeEnemyMonster.currentHp;

    if (this.#monsterCaptured) {
      // enemy monster was captured — advance the onboarding objective tracker
      dataManager.recordMonsterCaught();
      // advance any "catch" daily quests on the server (no-op offline / walletless)
      const caughtMonsterId =
        this.#sceneData.enemyMonsters?.[this.#activeEnemyMonsterPartyIndex]?.monsterId ??
        this.#sceneData.enemyMonsters?.[0]?.monsterId;
      void postQuestProgress(netManager.wallet, 'monster_caught', caughtMonsterId);
      // Server-authoritative capture reward: if this was a session-backed wild battle, resolve the
      // single-use session as a 'capture' so the server grants the reward (replay-proof) and we
      // reconcile the HUD to its number. No-op offline / non-session (HUD just isn't bumped) — the
      // capture flow / animation / state machine below is unchanged.
      const captureSessionId = this.#sceneData.battleSessionId;
      if (captureSessionId) {
        void resolveBattleSession(netManager.wallet, captureSessionId, 'capture', caughtMonsterId).then((res) => {
          if (res && typeof res.gold === 'number') {
            dataManager.setGold(res.gold);
          }
        });
      }
      this.#activeEnemyMonster.playDeathAnimation(() => {
        this.#showMessagesAndWaitForInput([`You caught ${this.#activeEnemyMonster.name}.`], () => {
          this.#battleStateMachine.setState(BATTLE_STATES.GAIN_EXPERIENCE);
        });
      });
      return;
    }

    if (this.#activeEnemyMonster.isFainted) {
      // if trainer battle, update ui to show monster fainted
      if (this.#isTrainerBattle) {
        /** @type {Phaser.GameObjects.Image} */
        (this.#availableMonstersUiContainerForNpc.getAt(this.#activeEnemyMonsterPartyIndex)).setAlpha(0.4);
      }

      // play monster fainted animation and wait for animation to finish (in the cave, the ceiling
      // crashes down on the foe first for a dramatic finishing blow)
      const runEnemyDeath = () =>
        this.#activeEnemyMonster.playDeathAnimation(() => {
          const text = this.#isTrainerBattle
            ? `${this.#activeEnemyMonster.name} has been knocked out.`
            : `Wild ${this.#activeEnemyMonster.name} fainted.`;
          this.#showMessagesAndWaitForInput([text], () => {
            this.#battleStateMachine.setState(BATTLE_STATES.GAIN_EXPERIENCE);
          });
        });
      if (this.#inCave) this.#caveCrush(this.#activeEnemyMonster, runEnemyDeath);
      else runEnemyDeath();
      return;
    }

    if (this.#activePlayerMonster.isFainted) {
      // play monster fainted animation and wait for animation to finish (cave: crushed by falling rock)
      const runPlayerDeath = () =>
      this.#activePlayerMonster.playDeathAnimation(() => {
        // update ui to show monster fainted
        /** @type {Phaser.GameObjects.Image} */
        (this.#availableMonstersUiContainerForPlayer.getAt(this.#activePlayerMonsterPartyIndex)).setAlpha(0.4);

        // check to see if we have other monsters that are able to battle
        const activeId = this.#sceneData.playerMonsters[this.#activePlayerMonsterPartyIndex]?.id;
        const hasOtherActiveMonsters = this.#sceneData.playerMonsters.some(
          (monster) => monster && monster.id !== activeId && monster.currentHp > 0,
        );

        // if not, player faints and battle is over
        if (!hasOtherActiveMonsters) {
          const text = this.#isTrainerBattle
            ? `${this.#sceneData.npc.name} has won the battle!`
            : 'You have no more monsters, escaping to safety...';

          playSoundFx(this, AUDIO_ASSET_KEYS.GAME_OVER); // deep "game over" voice on a full defeat
          this.#showMessagesAndWaitForInput([`${this.#activePlayerMonster.name} fainted.`, text], () => {
            this.#playerKnockedOut = true;
            this.#battleStateMachine.setState(BATTLE_STATES.FINISHED);
          });
          return;
        }

        // we have active monsters, so show message about monster fainting and then show monster party scene
        // so player can choose next monster
        this.#showMessagesAndWaitForInput(
          [`${this.#activePlayerMonster.name} fainted.`, 'Choose another monster to continue the battle.'],
          () => {
            this.#activeMonsterKnockedOut = true;
            this.#battleStateMachine.setState(BATTLE_STATES.SWITCH_MONSTER);
          }
        );
      });
      if (this.#inCave) this.#caveCrush(this.#activePlayerMonster, runPlayerDeath);
      else runPlayerDeath();
      return;
    }

    this.#battleStateMachine.setState(BATTLE_STATES.PLAYER_INPUT);
  }

  /**
   * Award Gold for winning this battle. Computes a local estimate from the defeated enemy's
   * level for instant feedback (and so single-player / offline still rewards Gold), shows a
   * "+N Gold" flash, then reconciles with the server-authoritative balance from
   * POST /player/battle-win when connected. Idempotent per battle.
   * @returns {void}
   */
  #awardBattleWinGold() {
    if (this.#goldAwarded) return;
    this.#goldAwarded = true;

    // advance the onboarding objective tracker (winning a battle)
    dataManager.recordBattleWon();

    const enemyLevel = Math.max(1, this.#activeEnemyMonster?.level ?? this.#sceneData.enemyMonsters?.[0]?.currentLevel ?? 1);
    const before = dataManager.getGold();

    // Bump account XP live so the player card's Lv/XP move on each win (mirrors the server's
    // awardBattleGold XP grant; the server stays authoritative and reconciles on next load).
    const curXp = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_XP) || 0;
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_XP, curXp + enemyLevel * XP_PER_ENEMY_LEVEL);

    // Show the gold gain from the SERVER's AUTHORITATIVE credit (after the streak/zone/booster
    // multipliers + the hourly cap), NOT a client guess — players were seeing an inflated number
    // (e.g. +150) then actually receiving less (+80). We now wait for the server's new balance and
    // flash the REAL delta. Fall back to a local estimate only when offline / no server number.
    const localReward = 10 + enemyLevel * 5;
    const applyGold = (newBalance) => {
      if (typeof newBalance === 'number') {
        const gained = Math.max(0, newBalance - before);
        dataManager.setGold(newBalance);
        if (gained > 0) this.#showGoldFlash(gained);
      } else {
        dataManager.setGold(before + localReward);
        this.#showGoldFlash(localReward);
      }
    };

    // Prefer the single-use battle session (server-known enemy + reward, replay-proof) when this was a
    // session-backed wild battle; otherwise the legacy /player/battle-win path. Either way the flash
    // reflects the server's real credit; on any failure we degrade to the optimistic local estimate.
    const wallet = netManager.wallet;
    const sessionId = this.#sceneData.battleSessionId;
    if (sessionId) {
      void resolveBattleSession(wallet, sessionId, 'win')
        .then((res) => {
          if (res && typeof res.gold === 'number') applyGold(res.gold);
          else void postBattleWin(wallet, enemyLevel).then((gold) => applyGold(typeof gold === 'number' ? gold : null));
        })
        .catch(() => applyGold(null));
    } else {
      void postBattleWin(wallet, enemyLevel)
        .then((gold) => applyGold(typeof gold === 'number' ? gold : null))
        .catch(() => applyGold(null));
    }

    // advance any matching daily quests ("Defeat N <species>" / "Scout the Wilds") on the server.
    // Degrades to a no-op offline / walletless. The defeated species id lets the server match
    // species-specific quests; generic "defeat any" quests advance regardless.
    const defeatedEnemy =
      this.#sceneData.enemyMonsters?.[this.#activeEnemyMonsterPartyIndex] ?? this.#sceneData.enemyMonsters?.[0];
    const defeatedMonsterId = defeatedEnemy?.monsterId;
    void postQuestProgress(wallet, 'battle_win', defeatedMonsterId);
  }

  /**
   * Brief "+N Gold" flash on the victory screen — floats up and fades near the top-center.
   * @param {number} amount
   * @returns {void}
   */
  #showGoldFlash(amount) {
    const x = BATTLE_DESIGN_WIDTH / 2;
    const y = 90;
    const flash = this.add
      .text(x, y, `+${amount} Gold`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: '#f5b23e',
        stroke: '#3a2705',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(5000)
      .setShadow(0, 0, '#f5b23e', 18, true, true)
      .setScale(0.6)
      .setAlpha(0);
    this.tweens.add({ targets: flash, alpha: 1, scale: 1, y: y - 10, duration: 320, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: flash,
      alpha: 0,
      y: y - 60,
      delay: 1500,
      duration: 700,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Scales + centers the fixed 1024x576 design-space battle layout so it covers the actual
   * viewport at any window size (and re-runs on RESIZE). Uses the larger of the width/height
   * ratios so the design space fully covers the screen with no black bars, then scrolls the
   * camera so the design space is centered. The bottom info pane / menu are authored against the
   * same design constants, so they stay aligned with the sprites under this transform.
   * @returns {void}
   */
  #fitCameraToViewport() {
    const viewportWidth = this.scale.width;
    const viewportHeight = this.scale.height;
    const cam = this.cameras.main;
    // CONTAIN fit: scale the 1024x576 design space so the WHOLE battle (both monsters, both HP
    // panels, and the full bottom action menu + "what should X do next" prompt) is always visible
    // at any aspect ratio. Using the smaller ratio guarantees nothing is cropped; the leftover
    // letterbox margins are filled with a solid dark camera background so there are no bright bars.
    const zoom = Math.min(viewportWidth / BATTLE_DESIGN_WIDTH, viewportHeight / BATTLE_DESIGN_HEIGHT);
    cam.setZoom(zoom);
    // dark letterbox fill behind everything (the area outside the 1024x576 design space)
    cam.setBackgroundColor('#05080d');
    // center the design space within the viewport
    cam.centerOn(BATTLE_DESIGN_WIDTH / 2, BATTLE_DESIGN_HEIGHT / 2);
  }

  /**
   * @returns {void}
   */
  #transitionToNextScene() {
    /** @type {import('./world-scene.js').WorldSceneData} */
    const sceneDataToPass = {
      isPlayerKnockedOut: this.#playerKnockedOut,
    };
    // Return to the world on the fade event OR a browser-timer fallback (whichever fires first), so a
    // missed FADE_OUT_COMPLETE can never strand the player on a black screen at battle end. The window
    // timer is independent of the scene clock, so it fires even if the scene update stalls.
    this.cameras.main.fadeOut(600, 0, 0, 0);
    let returned = false;
    const go = () => {
      if (returned) return;
      returned = true;
      this.scene.start(SCENE_KEYS.WORLD_SCENE, sceneDataToPass);
    };
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, go);
    window.setTimeout(go, 700);
  }

  /**
   * @returns {void}
   */
  #createBattleStateMachine() {
    /**
     * General state flow for battle scene
     *
     * scene transition to the battle menu
     * battle states
     * intro -> setup everything that is needed
     * pre-battle-npc -> animations as characters appear
     * pre-battle -> animations as monsters appear
     * monster info text renders onto the page & wait for player input
     * any key press, and now menu stuff shows up
     * player_turn -> choose what to do, wait for input from player
     * enemy_turn -> random choice,
     * battle_fight -> enemy and player options evaluated, play each attack animation
     * battle_fight_post_check -> see if one of the characters died, repeat
     *
     * // TODO:FUTURE update with latest set of states
     */

    this.#battleStateMachine = new StateMachine('battle', this);

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.INTRO,
      onEnter: () => {
        // wait for any scene setup and transitions to complete
        createSceneTransition(this, {
          skipSceneTransition: this.#skipAnimations,
          callback: () => {
            if (this.#isTrainerBattle) {
              this.#battleStateMachine.setState(BATTLE_STATES.PRE_BATTLE_INFO_NPC);
              return;
            }
            this.#battleStateMachine.setState(BATTLE_STATES.PRE_BATTLE_INFO);
          },
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.PRE_BATTLE_INFO_NPC,
      onEnter: async () => {
        // wait for enemy npc to appear on screen and notify player they want to battle
        await this.#enemyBattleNpc.playAppearAnimation();

        // wait for text animation to complete and move to next state
        this.#showMessagesAndWaitForInput([`${this.#sceneData.npc.name} would like to battle!`], () => {
          // hide npc as they bring out their monster
          this.#enemyBattleNpc.hide();
          this.#battleStateMachine.setState(BATTLE_STATES.PRE_BATTLE_INFO);
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.PRE_BATTLE_INFO,
      onEnter: () => {
        // wait for enemy monster to appear on the screen and notify player about the wild monster
        this.#activeEnemyMonster.playMonsterAppearAnimation(() => {
          // don't wait for health bar to appear, play at the same time
          this.#activeEnemyMonster.playMonsterHealthBarAppearAnimation(() => {
            // if this is a trainer battle show number of enemies
            if (this.#isTrainerBattle) {
              this.#availableMonstersUiContainerForNpc.setAlpha(1);
            }
          });

          const text = this.#isTrainerBattle
            ? `${this.#sceneData.npc.name} brought out ${this.#activeEnemyMonster.name}.`
            : `wild ${this.#activeEnemyMonster.name} appeared!`;
          this.#showMessagesAndWaitForInput([text], () => {
            // wait for text animation to complete and move to next state
            this.#battleStateMachine.setState(BATTLE_STATES.BRING_OUT_MONSTER);
          });
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.BRING_OUT_MONSTER,
      onEnter: () => {
        // wait for player monster to appear on the screen and notify the player about monster
        this.#activePlayerMonster.playMonsterAppearAnimation(() => {
          this.#activePlayerMonster.playMonsterHealthBarAppearAnimation(() => {
            this.#availableMonstersUiContainerForPlayer.setAlpha(1);
          });
          this.#battleMenu.updateInfoPaneMessageNoInputRequired(`go ${this.#activePlayerMonster.name}!`, () => {
            // wait for text animation to complete and move to next state
            this.time.delayedCall(1200, () => {
              if (this.#switchingActiveMonster && !this.#activeMonsterKnockedOut) {
                this.#battleStateMachine.setState(BATTLE_STATES.ENEMY_INPUT);
                return;
              }

              this.#switchingActiveMonster = false;
              this.#activeMonsterKnockedOut = false;
              this.#battleStateMachine.setState(BATTLE_STATES.PLAYER_INPUT);
            });
          });
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.PLAYER_INPUT,
      onEnter: () => {
        this._controls.lockInput = false;
        this.#battleMenu.showMainBattleMenu();
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.ENEMY_INPUT,
      onEnter: () => {
        // pick a random move for the enemy monster, and in the future implement some type of AI behavior
        this.#activeEnemyAttackIndex = this.#activeEnemyMonster.pickRandomMove();
        this.#battleStateMachine.setState(BATTLE_STATES.BATTLE);
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.BATTLE,
      onEnter: () => {
        // general battle flow
        // show attack used, brief pause
        // then play attack animation, brief pause
        // then play damage animation, brief pause
        // then play health bar animation, brief pause
        // then repeat the steps above for the other monster

        // if item was used, only have enemy attack
        if (this.#battleMenu.wasItemUsed) {
          this.#battleStateMachine.setState(BATTLE_STATES.USED_ITEM);
          return;
        }

        // if player failed to flee, only have enemy attack
        if (this.#battleMenu.isAttemptingToFlee) {
          this.time.delayedCall(500, () => {
            this.#enemyAttack(() => {
              this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
            });
          });
          return;
        }

        // if player switched active monster, only have enemy attack
        if (this.#switchingActiveMonster) {
          this.time.delayedCall(500, () => {
            this.#enemyAttack(() => {
              this.#switchingActiveMonster = false;
              this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
            });
          });
          return;
        }

        const randomNumber = Phaser.Math.Between(0, 1);
        if (randomNumber === 0) {
          this.#playerAttack(() => {
            this.#enemyAttack(() => {
              this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
            });
          });
          return;
        }

        this.#enemyAttack(() => {
          this.#playerAttack(() => {
            this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
          });
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.POST_ATTACK_CHECK,
      onEnter: () => {
        this.#postBattleSequenceCheck();
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.FINISHED,
      onEnter: () => {
        // update the data manager with latest monster data
        dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, this.#sceneData.playerMonsters);
        // Persist the leveled-up party to the server IMMEDIATELY — don't wait for the world's on-create
        // autosave (async + skipped when partyFromCacheOnly), which left a window where a refresh right
        // after a battle lost the level-up and the OLD party reloaded ("why did my level drop"). Local
        // save + a direct server save; guarded like the autosave so a stale cache load can't clobber.
        if (netManager.wallet && !netManager.partyFromCacheOnly) {
          dataManager.saveData();
          const storage = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE);
          void saveParty(
            netManager.wallet,
            this.#sceneData.playerMonsters,
            Array.isArray(storage) && storage.length > 0 ? storage : undefined,
          ).catch(() => {});
        }
        // update the data manager with the npc that was defeated
        if (this.#isTrainerBattle && this.#sceneData.npc !== undefined && this.#sceneData.npc.id !== undefined) {
          dataManager.addDefeatedNpc(this.#sceneData.npc.id);
        }
        this.#transitionToNextScene();
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.FLEE_ATTEMPT,
      onEnter: () => {
        const randomNumber = Phaser.Math.Between(1, 10);
        if (randomNumber > 5) {
          // player has run away successfully
          this.#showMessagesAndWaitForInput(['You got away safely!'], () => {
            this.time.delayedCall(200, () => {
              playSoundFx(this, AUDIO_ASSET_KEYS.FLEE);
              this.#battleStateMachine.setState(BATTLE_STATES.FINISHED);
            });
          });
          return;
        }
        // player failed to run away, allow enemy to take their turn
        this.#showMessagesAndWaitForInput(['You failed to run away...'], () => {
          this.time.delayedCall(200, () => {
            this.#battleStateMachine.setState(BATTLE_STATES.ENEMY_INPUT);
          });
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.GAIN_EXPERIENCE,
      onEnter: () => {
        // The player WON (an enemy fainted / was captured) — award Gold for the victory.
        // Runs once per battle (guarded). Server computes the amount; we add locally for
        // instant feedback and a "+N Gold" flash, then reconcile with the authoritative value.
        this.#awardBattleWinGold();

        // update exp bar based on experience gained, then transition to finished state
        const gainedExpForActiveMonster = calculateExpGainedFromMonster(
          this.#activeEnemyMonster.baseExpValue,
          this.#activeEnemyMonster.level,
          true
        );
        const gainedExpForInactiveMonster = calculateExpGainedFromMonster(
          this.#activeEnemyMonster.baseExpValue,
          this.#activeEnemyMonster.level,
          false
        );

        /** @type {string[]} */
        const messages = [];
        let didActiveMonsterLevelUp = false;
        this.#sceneData.playerMonsters.forEach((monster, index) => {
          // ensure only monsters that are not knocked out gain exp
          if (this.#sceneData.playerMonsters[index].currentHp <= 0) {
            return;
          }

          /** @type {import('../utils/leveling-utils.js').StatChanges} */
          let statChanges;
          if (index === this.#activePlayerMonsterPartyIndex) {
            statChanges = this.#activePlayerMonster.updateMonsterExp(gainedExpForActiveMonster);
            if (statChanges.level !== 0) {
              didActiveMonsterLevelUp = true;
            }
          } else {
            statChanges = handleMonsterGainingExperience(
              this.#sceneData.playerMonsters[index],
              gainedExpForInactiveMonster
            );
          }
          // Per-beast "gained N exp" lines are dropped — with a 6-beast party that was 6 taps to clear
          // every fight. We show ONE team summary (below) + a single combined line per beast that
          // actually leveled, so a no-level-up win is a single tap.
          if (statChanges !== undefined && statChanges.level !== 0) {
            const m = this.#sceneData.playerMonsters[index];
            const line = `${m.name} grew to Lv. ${m.currentLevel}!  +${statChanges.attack} ATK, +${statChanges.health} HP`;
            if (index === this.#activePlayerMonsterPartyIndex) messages.unshift(line);
            else messages.push(line);
          }
        });
        messages.unshift('Your team gained experience!');

        this._controls.lockInput = true;
        this.#activePlayerMonster.updateMonsterExpBar(didActiveMonsterLevelUp, false, () => {
          this.#showMessagesAndWaitForInput(messages, () => {
            this.time.delayedCall(200, () => {
              if (this.#monsterCaptured) {
                this.#battleStateMachine.setState(BATTLE_STATES.CAUGHT_MONSTER);
                return;
              }

              /**
               * When a monster faints, we need to give experience to the active monster.
               * After giving experience, we need to check if there are more active monsters,
               * and if so we need to have the npc bring out the next monster. If no more
               * monsters, then we can go to the end of the battle.
               */

              // check to see if there are more active monsters
              const hasOtherActiveMonsters = this.#sceneData.enemyMonsters.some((monster) => {
                return (
                  monster.id !== this.#sceneData.enemyMonsters[this.#activeEnemyMonsterPartyIndex].id &&
                  monster.currentHp > 0
                );
              });

              if (hasOtherActiveMonsters) {
                // if npc has other monsters, we need to switch to the next monster in their party
                this.#battleStateMachine.setState(BATTLE_STATES.NPC_SWITCH_MONSTER);
                return;
              }

              // if npc battle have npc re-appear and show message to player
              if (this.#isTrainerBattle) {
                this.#activePlayerMonster.playDeathAnimation(async () => {
                  this.#availableMonstersUiContainerForPlayer.setAlpha(0);
                  this.#availableMonstersUiContainerForNpc.setAlpha(0);
                  await this.#enemyBattleNpc.playAppearAnimation();
                  this.#showMessagesAndWaitForInput(this.#sceneData.npc.trainerLostMessages, () => {
                    this.#battleStateMachine.setState(BATTLE_STATES.FINISHED);
                  });
                });
                return;
              }

              // if no more monsters, go to the end of the battle
              this.#battleStateMachine.setState(BATTLE_STATES.FINISHED);
            });
          });
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.SWITCH_MONSTER,
      onEnter: () => {
        // check to see if the player has other monsters they can switch to
        const activeId = this.#sceneData.playerMonsters[this.#activePlayerMonsterPartyIndex]?.id;
        const hasOtherActiveMonsters = this.#sceneData.playerMonsters.some(
          (monster) => monster && monster.id !== activeId && monster.currentHp > 0,
        );
        if (!hasOtherActiveMonsters) {
          this.#showMessagesAndWaitForInput(['You have no other monsters able to fight in your party'], () => {
            this.#battleStateMachine.setState(BATTLE_STATES.PLAYER_INPUT);
          });
          return;
        }

        // otherwise, there are other available monsters to switch to, need to show ui so player can select monster
        // pause this scene and launch the monster party scene
        /** @type {import('./monster-party-scene.js').MonsterPartySceneData} */
        const sceneDataToPass = {
          previousSceneName: SCENE_KEYS.BATTLE_SCENE,
          activeBattleMonsterPartyIndex: this.#activePlayerMonsterPartyIndex,
          activeMonsterKnockedOut: this.#activeMonsterKnockedOut,
        };
        this.scene.launch(SCENE_KEYS.MONSTER_PARTY_SCENE, sceneDataToPass);
        this.scene.pause(SCENE_KEYS.BATTLE_SCENE);
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.NPC_SWITCH_MONSTER,
      onEnter: () => {
        // npc has other monsters that can be switched to, for now npc will just switch to the next monster in the battle line
        // TODO:FUTURE this could be enhanced if we wanted the npc to pick a random monster, choose one based on the monster
        // the player has out, etc.
        this.#activeEnemyMonsterPartyIndex += 1;

        // show text about bringing out next monster
        const nextMonster = this.#sceneData.enemyMonsters[this.#activeEnemyMonsterPartyIndex];
        this.#showMessagesAndWaitForInput([`Foe is about to send in ${nextMonster.name}.`], () => {
          this.#activeEnemyMonster.switchMonster(nextMonster);
          // have monster appear, and show updated health bar
          this.#activeEnemyMonster.playMonsterAppearAnimation(() => {
            this.#activeEnemyMonster.playMonsterHealthBarAppearAnimation(() => {
              // transition to player input state
              this.#availableMonstersUiContainerForNpc.setAlpha(1);
              this.#battleStateMachine.setState(BATTLE_STATES.PLAYER_INPUT);
            });
          });
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.USED_ITEM,
      onEnter: () => {
        // NEVER throw here (exhaustiveGuard did → the state hung forever): `itemUsed` can be
        // undefined, and the Gold Item Shop added new items (Premium Ball / Revive / Potion) whose
        // category may not be the original CAPTURE/HEAL enum. Only a CAPTURE ball goes to the
        // capture sequence; everything else takes the safe HEAL path (re-reads HP + runs the enemy
        // turn → POST_ATTACK_CHECK), so the battle always progresses.
        if (this.#battleMenu.itemUsed?.category === ITEM_CATEGORY.CAPTURE) {
          this.#battleStateMachine.setState(BATTLE_STATES.CAPTURE_ITEM_USED);
        } else {
          this.#battleStateMachine.setState(BATTLE_STATES.HEAL_ITEM_USED);
        }
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.HEAL_ITEM_USED,
      onEnter: () => {
        this.#activePlayerMonster.updateMonsterHealth(
          /** @type {import('../types/typedef.js').Monster[]} */ (
            dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY)
          )[this.#activePlayerMonsterPartyIndex].currentHp
        );
        this.time.delayedCall(500, () => {
          this.#enemyAttack(() => {
            this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
          });
        });
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.CAPTURE_ITEM_USED,
      onEnter: async () => {
        // we throw the monster ball to attempt to capture the monster
        // play ball shake animation depending on success of capture
        // 3 shakes - caught, 2 shakes - off by 10, 1 shakes off by 30, 0 shakes off by more than 30
        // 1. throw ball animation, 2. shake ball animation, 3. break animation if needed, 4. monster re-appear if needed

        // CRITICAL anti-freeze: each capture animation is a promise-wrapped tween. If ANY of them
        // never resolves (a tween onComplete that doesn't fire), this async onEnter hangs forever
        // with input locked → the battle freezes ("freezing whilst trying to catch them"). Race
        // every await against a timeout so the capture sequence ALWAYS progresses, and wrap the whole
        // thing so any throw still advances the state instead of hanging.
        const withTimeout = (p, ms) =>
          Promise.race([Promise.resolve(p).catch(() => {}), new Promise((res) => setTimeout(res, ms))]);
        try {
          // Premium Ball (CAPTURE_2) gets a +20 capture bonus over the basic ball.
          const captureBonus = this.#battleMenu.itemUsed?.effect === 'CAPTURE_2' ? 20 : 0;
          const captureResults = calculateMonsterCaptureResults(this.#activeEnemyMonster, captureBonus);
          const diffInCapture = captureResults.requiredCaptureValue - captureResults.actualCaptureValue;
          let numberOfShakes = 0;
          if (diffInCapture <= 10) {
            numberOfShakes = 2;
          } else if (diffInCapture <= 30) {
            numberOfShakes = 1;
          }
          if (captureResults.wasCaptured) {
            numberOfShakes = 3;
          }

          await withTimeout(this.#ball.playThrowBallAnimation(), 2500);
          await withTimeout(this.#activeEnemyMonster.playCatchAnimation(), 2500);
          await withTimeout(this.#ball.playShakeBallAnimation(Math.max(0, numberOfShakes - 1)), 3000);

          if (captureResults.wasCaptured) {
            this.#monsterCaptured = true;
            this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
            return;
          }

          await withTimeout(sleep(500, this), 1000);
          this.#ball.hide();
          await withTimeout(this.#activeEnemyMonster.playCatchAnimationFailed(), 2500);

          this.#showMessagesAndWaitForInput(['The wild monster breaks free!'], () => {
            this.time.delayedCall(500, () => {
              this.#enemyAttack(() => {
                this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
              });
            });
          });
        } catch (err) {
          console.warn('[battle] capture flow error — recovering', err);
          this._controls.lockInput = false;
          this.#battleStateMachine.setState(BATTLE_STATES.POST_ATTACK_CHECK);
        }
      },
    });

    this.#battleStateMachine.addState({
      name: BATTLE_STATES.CAUGHT_MONSTER,
      onEnter: () => {
        // add monster to the players party
        /** @type {import('../types/typedef.js').Monster} */
        const updatedMonster = {
          ...this.#sceneData.enemyMonsters[0],
          id: generateUuid(),
          currentHp: this.#activeEnemyMonster.currentHp,
        };
        // Party caps at 6. When it's full the catch goes to the PC box instead of being lost — persist
        // it straight to storage (the world only re-saves the 6-slot party on return).
        const MAX_PARTY = 6;
        if (this.#sceneData.playerMonsters.length >= MAX_PARTY) {
          const storage = [...(dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE) || [])];
          storage.push(updatedMonster);
          dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE, storage);
          this.#battleMenu.updateInfoPaneMessagesAndWaitForInput(
            [`Your team is full — ${updatedMonster.name} was sent to your PC box!`],
            () => this.#battleStateMachine.setState(BATTLE_STATES.FINISHED)
          );
          return;
        }
        this.#sceneData.playerMonsters.push(updatedMonster);

        this.#battleStateMachine.setState(BATTLE_STATES.FINISHED);
      },
    });

    // start state machine
    this.#battleStateMachine.setState(BATTLE_STATES.INTRO);
  }

  /**
   * Lightweight ambient life on the battle backdrop — a few birds gliding across the sky and motes of
   * pollen/dust rising through the light, so the arena reads as alive instead of a static image. Pure
   * procedural graphics (no art); everything loops forever, is cheap, and sits behind the monsters.
   * @returns {void}
   */
  /**
   * Cave finishing-blow: the ceiling above the fainting monster cracks and boulders crash down on it
   * (a dramatic underground KO). Runs on BOTH a win and a loss, then calls onDone so the normal faint
   * animation follows. Skipped (instant onDone) when battle animations are off.
   * @param {import('../battle/monsters/battle-monster.js').BattleMonster} monster
   * @param {() => void} onDone
   */
  #caveCrush(monster, onDone) {
    const sprite = monster?.sprite;
    if (!sprite || this.#skipAnimations) {
      onDone?.();
      return;
    }
    const cx = sprite.x;
    const cy = sprite.y;
    const rocks = [];
    for (let i = 0; i < 8; i += 1) {
      const size = Phaser.Math.Between(16, 40);
      const rock = this.add
        .rectangle(cx + Phaser.Math.Between(-80, 80), cy - 420, size, size, i % 2 ? 0x5a4f43 : 0x6b5d4f)
        .setDepth(200)
        .setAngle(Phaser.Math.Between(0, 360))
        .setStrokeStyle(3, 0x342c24, 1);
      rocks.push(rock);
      this.tweens.add({
        targets: rock,
        y: cy + Phaser.Math.Between(-12, 36),
        angle: rock.angle + Phaser.Math.Between(-160, 160),
        duration: Phaser.Math.Between(360, 600),
        delay: i * 45,
        ease: 'Quad.easeIn',
        onComplete: () => this.#rockDust(rock.x, rock.y + size / 2),
      });
    }
    // impact: a LOCALIZED jitter on the beast being crushed (not a full-screen camera shake — players
    // asked for just the cave around it to shake, then the rocks land on top). The UI/camera stay still.
    this.time.delayedCall(420, () => {
      if (!sprite.active) return;
      this.tweens.add({
        targets: sprite,
        x: cx + 7,
        duration: 45,
        yoyo: true,
        repeat: 5,
        ease: 'Sine.easeInOut',
        onComplete: () => sprite.active && sprite.setX(cx),
      });
    });
    this.time.delayedCall(1050, () => {
      this.tweens.add({
        targets: rocks,
        alpha: 0,
        duration: 480,
        onComplete: () => {
          rocks.forEach((r) => r.destroy());
          onDone?.();
        },
      });
    });
  }

  /** A quick burst of dust where a boulder lands. */
  #rockDust(x, y) {
    for (let i = 0; i < 5; i += 1) {
      const d = this.add.circle(x, y, Phaser.Math.Between(5, 11), 0x8c7d6a, 0.6).setDepth(199);
      this.tweens.add({
        targets: d,
        x: x + Phaser.Math.Between(-46, 46),
        y: y - Phaser.Math.Between(8, 40),
        alpha: 0,
        scale: 1.7,
        duration: Phaser.Math.Between(360, 560),
        ease: 'Quad.easeOut',
        onComplete: () => d.destroy(),
      });
    }
  }

  #createBattleAmbience(inCave = false) {
    const W = 1024;
    const H = 576;
    const rand = (a, b) => a + Math.random() * (b - a);
    // birds: small dark V silhouettes that flap + glide across the upper sky (skipped underground)
    for (let i = 0; !inCave && i < 3; i += 1) {
      const g = this.add.graphics().setDepth(0).setAlpha(0.45);
      let flap = false;
      const draw = () => {
        g.clear();
        g.lineStyle(2, 0x2b2b3a, 0.7);
        const w = 7;
        const d = flap ? 2 : 6;
        g.beginPath();
        g.moveTo(-w, 0);
        g.lineTo(0, -d);
        g.lineTo(w, 0);
        g.strokePath();
      };
      draw();
      this.time.addEvent({
        delay: 280,
        loop: true,
        callback: () => {
          flap = !flap;
          draw();
        },
      });
      const fly = () => {
        g.setPosition(-30, rand(28, 150));
        this.tweens.add({
          targets: g,
          x: W + 40,
          y: `+=${rand(-18, 18)}`,
          duration: rand(8000, 13000),
          delay: i === 0 ? rand(0, 2000) : rand(0, 6000),
          onComplete: fly,
        });
      };
      fly();
    }
    // pollen / dust motes rising through the light
    for (let i = 0; i < 16; i += 1) {
      const m = this.add.circle(rand(0, W), rand(120, H), rand(1, 2.4), inCave ? 0xffc18a : 0xfff3c4, 0.5).setDepth(0);
      const drift = () => {
        this.tweens.add({
          targets: m,
          y: `-=${rand(70, 150)}`,
          x: `+=${rand(-40, 40)}`,
          alpha: 0,
          duration: rand(4500, 8500),
          onComplete: () => {
            m.setPosition(rand(0, W), H + rand(0, 30));
            m.setAlpha(rand(0.3, 0.6));
            drift();
          },
        });
      };
      drift();
    }
  }

  /**
   * @param {Phaser.Scenes.Systems} sys
   * @param {BattleSceneWasResumedData | undefined} [data]
   * @returns {void}
   */
  handleSceneResume(sys, data) {
    super.handleSceneResume(sys, data);

    if (!data || !data.wasMonsterSelected || data.selectedMonsterIndex === undefined) {
      this.#battleStateMachine.setState(BATTLE_STATES.PLAYER_INPUT);
      return;
    }

    this._controls.lockInput = true;
    this.#switchingActiveMonster = true;

    // if monster was selected, then we need to play animation for switching monsters and let enemy monster attack
    // if previous monster was not knocked out
    this.#activePlayerMonster.playDeathAnimation(() => {
      this.#activePlayerMonsterPartyIndex = data.selectedMonsterIndex;
      this.#activePlayerMonster.switchMonster(this.#sceneData.playerMonsters[data.selectedMonsterIndex]);
      this.#battleMenu.updateMonsterAttackSubMenu();
      this._controls.lockInput = false;
      this.#battleStateMachine.setState(BATTLE_STATES.BRING_OUT_MONSTER);
    });
  }

  /**
   * Creates the UI component that shows the ball icons that represent the available monsters in the
   * players current party. A transparent ball means a monster is knocked out, while a non-transparent
   * ball means the monster is eligible to battle.
   *
   * Updated to support showing ball icons that represent the available monsters in the npc party as well.
   * @returns {void}
   */
  #createAvailableMonstersUi() {
    this.#availableMonstersUiContainerForPlayer = this.add.container(BATTLE_DESIGN_WIDTH - 24, 304, []);
    this.#sceneData.playerMonsters.forEach((monster, index) => {
      if (!monster) return; // a null/empty party slot must not crash the battle UI
      const alpha = monster.currentHp > 0 ? 1 : 0.4;
      const ball = this.add
        .image(30 * -index, 0, BATTLE_ASSET_KEYS.BALL_THUMBNAIL, 0)
        .setScale(0.8)
        .setAlpha(alpha);
      this.#availableMonstersUiContainerForPlayer.add(ball);
    });
    this.#availableMonstersUiContainerForPlayer.setAlpha(0);

    // add logic to show available enemy monsters during trainer battles
    if (this.#isTrainerBattle) {
      this.#availableMonstersUiContainerForNpc = this.add.container(24, 116, []);
      this.#sceneData.enemyMonsters.forEach((monster, index) => {
        if (!monster) return;
        const alpha = monster.currentHp > 0 ? 1 : 0.4;
        const ball = this.add
          .image(30 * index, 0, BATTLE_ASSET_KEYS.BALL_THUMBNAIL, 0)
          .setScale(0.8)
          .setAlpha(alpha);
        this.#availableMonstersUiContainerForNpc.add(ball);
      });
      this.#availableMonstersUiContainerForNpc.setAlpha(0);
    }
  }

  /**
   * Wrapper for the battle menu method to show messages to the player were we
   * want to wait until the player has confirmed they have read the message. The
   * wrapper will handle locking and unlocking our input so player can interact
   * with the dialog window.
   * @param {string[]} messages array of messages to show the player
   * @param {() => void} callback function to invoke after all messages have been shown to the player and confirmed
   */
  #showMessagesAndWaitForInput(messages, callback) {
    this._controls.lockInput = false;
    this.#battleMenu.updateInfoPaneMessagesAndWaitForInput(messages, () => {
      this._controls.lockInput = true;
      callback();
    });
  }
}
